import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import { uploadImage as firebaseUploadImage } from '../firebase/config';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package, 
  CreditCard,
  Image as ImageIcon,
  X,
  ShoppingBag,
  Upload,
  Loader2,
  Copy,
  Tag,
  Percent,
  ImagePlus,
  FileImage,
  Phone,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  RefreshCw,
  Printer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - handles both relative and absolute URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminProducts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [customOrdersLoading, setCustomOrdersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [uploading, setUploading] = useState(false);
  const [updatingOrderStatus, setUpdatingOrderStatus] = useState({});
  const [viewingFiles, setViewingFiles] = useState(null); // order being previewed
  const [bulkPricingTiers, setBulkPricingTiers] = useState([]);
  const [loadingBulkPricing, setLoadingBulkPricing] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [tierForm, setTierForm] = useState({
    min_quantity: '',
    max_quantity: '',
    discount_percent: ''
  });
  
  // Check admin access
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  
  useEffect(() => {
    if (user && !isAdmin) {
      toast({ title: "Access Denied", description: "Only admin can access this page", variant: "destructive" });
      navigate('/');
    }
  }, [user, isAdmin, navigate, toast]);
  
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    mrp: '',
    price: '',
    discount_percent: '0',
    category: 'NFC Card',
    stock: '100',
    features: '',
    images: [],
    is_active: true
  });

  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_percent: '',
    product_id: '',
    min_order_value: '',
    max_discount: '',
    usage_limit: '',
    one_time_per_user: true,
    expiry_date: '',
    is_active: true,
    description: ''
  });

  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const token = await user?.getIdToken();
      
      // Load products
      const prodRes = await fetch(`${API_URL}/api/shop/products?active_only=false`);
      if (prodRes.ok) {
        const prodData = await prodRes.json();
        setProducts(prodData);
      }

      // Load orders (admin only)
      if (token) {
        const ordersRes = await fetch(`${API_URL}/api/shop/admin/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData);
        }

        // Load shop coupons
        const couponsRes = await fetch(`${API_URL}/api/shop/admin/coupons`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (couponsRes.ok) {
          const couponsData = await couponsRes.json();
          setCoupons(couponsData);
        }

        // Load custom design orders
        const customRes = await fetch(`${API_URL}/api/shop/admin/custom-design/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (customRes.ok) {
          setCustomOrders(await customRes.json());
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      mrp: (product.mrp || product.price).toString(),
      price: product.price.toString(),
      discount_percent: (product.discount_percent || 0).toString(),
      category: product.category || 'NFC Card',
      stock: (product.stock || 100).toString(),
      features: (product.features || []).join('\n'),
      images: product.images || [],
      is_active: product.is_active !== false
    });
    setShowProductDialog(true);
  };

  const openNewDialog = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      mrp: '',
      price: '',
      discount_percent: '0',
      category: 'NFC Card',
      stock: '100',
      features: '',
      images: [],
      is_active: true
    });
    setShowProductDialog(true);
  };

  const copyProduct = (product) => {
    // Set editingProduct to null so it creates a new product instead of updating
    setEditingProduct(null);
    setProductForm({
      name: `${product.name} (Copy)`,
      description: product.description || '',
      price: product.price.toString(),
      discount_percent: (product.discount_percent || 0).toString(),
      category: product.category || 'NFC Card',
      stock: (product.stock || 100).toString(),
      features: (product.features || []).join('\n'),
      images: product.images || [],
      is_active: product.is_active !== false
    });
    setShowProductDialog(true);
    toast({
      title: "Product copied",
      description: "Edit the details and save to create a new product"
    });
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast({
        title: "Missing fields",
        description: "Please fill in product name and price",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = await user.getIdToken();
      const payload = {
        name: productForm.name,
        description: productForm.description,
        mrp: parseFloat(productForm.mrp) || parseFloat(productForm.price),
        price: parseFloat(productForm.price),
        discount_percent: parseFloat(productForm.discount_percent) || 0,
        category: productForm.category,
        stock: parseInt(productForm.stock) || 100,
        features: productForm.features.split('\n').filter(f => f.trim()),
        images: productForm.images,
        is_active: productForm.is_active
      };

      const url = editingProduct 
        ? `${API_URL}/api/shop/admin/products/${editingProduct.id}`
        : `${API_URL}/api/shop/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: editingProduct ? "Product updated" : "Product created"
        });
        setShowProductDialog(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: "Product deleted" });
        loadData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  // Coupon CRUD functions
  const resetCouponForm = () => {
    setCouponForm({
      code: '',
      discount_percent: '',
      product_id: '',
      min_order_value: '',
      max_discount: '',
      usage_limit: '',
      one_time_per_user: true,
      expiry_date: '',
      is_active: true,
      description: ''
    });
  };

  const openCouponDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setCouponForm({
        code: coupon.code || '',
        discount_percent: coupon.discount_percent?.toString() || '',
        product_id: coupon.product_id || '',
        min_order_value: coupon.min_order_value?.toString() || '',
        max_discount: coupon.max_discount?.toString() || '',
        usage_limit: coupon.usage_limit?.toString() || '',
        one_time_per_user: coupon.one_time_per_user ?? true,
        expiry_date: coupon.expiry_date ? coupon.expiry_date.split('T')[0] : '',
        is_active: coupon.is_active ?? true,
        description: coupon.description || ''
      });
    } else {
      setEditingCoupon(null);
      resetCouponForm();
    }
    setShowCouponDialog(true);
  };

  const handleSaveCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_percent) {
      toast({
        title: "Validation Error",
        description: "Code and discount percentage are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = await user.getIdToken();
      const method = editingCoupon ? 'PUT' : 'POST';
      const url = editingCoupon 
        ? `${API_URL}/api/shop/admin/coupons/${editingCoupon.id}`
        : `${API_URL}/api/shop/admin/coupons`;

      const body = {
        code: couponForm.code.toUpperCase(),
        discount_percent: parseFloat(couponForm.discount_percent),
        product_id: couponForm.product_id || null,
        min_order_value: couponForm.min_order_value ? parseFloat(couponForm.min_order_value) : null,
        max_discount: couponForm.max_discount ? parseFloat(couponForm.max_discount) : null,
        usage_limit: couponForm.usage_limit ? parseInt(couponForm.usage_limit) : null,
        one_time_per_user: couponForm.one_time_per_user,
        expiry_date: couponForm.expiry_date ? new Date(couponForm.expiry_date).toISOString() : null,
        is_active: couponForm.is_active,
        description: couponForm.description || null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast({ title: editingCoupon ? "Coupon updated" : "Coupon created" });
        setShowCouponDialog(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save coupon');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: "Coupon deleted" });
        loadData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete coupon",
        variant: "destructive"
      });
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/admin/orders/${orderId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: "Order status updated" });
        loadData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive"
      });
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order #${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ 
          title: "Order Deleted", 
          description: `Order #${orderNumber} has been deleted`
        });
        // Remove from local state
        setOrders(orders.filter(o => o.id !== orderId));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete order');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete order",
        variant: "destructive"
      });
    }
  };

  // ---- Custom Design Order handlers ----
  const handleUpdateCustomOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrderStatus(p => ({ ...p, [orderId]: true }));
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/shop/custom-design/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast({ title: 'Status updated' });
        setCustomOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      } else {
        const e = await res.json();
        throw new Error(e.detail || 'Failed to update status');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setUpdatingOrderStatus(p => ({ ...p, [orderId]: false }));
    }
  };

  const handleRefreshCustomOrders = async () => {
    setCustomOrdersLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/shop/admin/custom-design/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCustomOrders(await res.json());
    } catch (e) { console.error(e); }
    finally { setCustomOrdersLoading(false); }
  };

  const handleDeleteCustomOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this custom design order? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/custom-design/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ 
          title: "Order Deleted", 
          description: "Custom design order has been deleted"
        });
        setCustomOrders(customOrders.filter(o => o.id !== orderId));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete order');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete order",
        variant: "destructive"
      });
    }
  };

  const getFileDownloadUrl = (fileId) => `${API_URL}/api/shop/custom-design/files/${fileId}`;

  // ============ Quantity Discount Functions ============
  const loadBulkPricingTiers = async () => {
    setLoadingBulkPricing(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/shop/admin/quantity-discounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setBulkPricingTiers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingBulkPricing(false); }
  };

  const openNewTierDialog = () => {
    setEditingTier(null);
    setTierForm({ min_quantity: '', max_quantity: '', discount_percent: '' });
    setShowTierDialog(true);
  };

  const openEditTierDialog = (tier) => {
    setEditingTier(tier);
    setTierForm({
      min_quantity: tier.min_quantity.toString(),
      max_quantity: tier.max_quantity ? tier.max_quantity.toString() : '',
      discount_percent: tier.discount_percent.toString()
    });
    setShowTierDialog(true);
  };

  const saveTier = async () => {
    const minQty = parseInt(tierForm.min_quantity);
    const maxQty = tierForm.max_quantity ? parseInt(tierForm.max_quantity) : null;
    const discountPercent = parseFloat(tierForm.discount_percent);
    
    if (isNaN(minQty) || minQty < 1) {
      toast({ title: "Invalid min quantity", variant: "destructive" });
      return;
    }
    if (isNaN(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      toast({ title: "Invalid discount percent (0-100)", variant: "destructive" });
      return;
    }

    try {
      const token = await user.getIdToken();
      const payload = { min_quantity: minQty, max_quantity: maxQty, discount_percent: discountPercent };
      
      if (editingTier) {
        const res = await fetch(`${API_URL}/api/shop/admin/quantity-discounts/${editingTier.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to update tier');
        toast({ title: "Discount tier updated" });
      } else {
        const res = await fetch(`${API_URL}/api/shop/admin/quantity-discounts`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to create tier');
        toast({ title: "Discount tier created" });
      }
      
      setShowTierDialog(false);
      loadBulkPricingTiers();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this discount tier?')) return;
    
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/shop/admin/quantity-discounts/${tierId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete tier');
      toast({ title: "Discount tier deleted" });
      loadBulkPricingTiers();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    try {
      for (const file of files) {
        try {
          // Upload to Firebase Storage
          const storagePath = `product-images/${Date.now()}-${file.name}`;
          const firebaseUrl = await firebaseUploadImage(file, storagePath);
          setProductForm(prev => ({
            ...prev,
            images: [...prev.images, firebaseUrl]
          }));
          toast({ title: "Image uploaded", description: file.name });
        } catch (fbErr) {
          console.error('Firebase upload failed, falling back to server:', fbErr);
          // Fallback to backend
          const token = await user.getIdToken();
          const formData = new FormData();
          formData.append('file', file);
          const response = await fetch(`${API_URL}/api/shop/admin/products/upload-image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          if (response.ok) {
            const data = await response.json();
            const fullUrl = `${API_URL}${data.url}`;
            setProductForm(prev => ({ ...prev, images: [...prev.images, fullUrl] }));
            toast({ title: "Image uploaded", description: file.name });
          } else {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to upload image');
          }
        }
      }
    } catch (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) { fileInputRef.current.value = ''; }
    }
  };

  const addImageUrl = () => {
    if (newImageUrl && !productForm.images.includes(newImageUrl)) {
      setProductForm(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <AdminNav />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Shop Management</h1>
            {activeTab === 'products' && (
              <Button onClick={openNewDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              variant={activeTab === 'products' ? 'default' : 'outline'}
              onClick={() => setActiveTab('products')}
            >
              <Package className="w-4 h-4 mr-2" />
              Products ({products.length})
            </Button>
            <Button
              variant={activeTab === 'orders' ? 'default' : 'outline'}
              onClick={() => setActiveTab('orders')}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Orders ({orders.length})
            </Button>
            <Button
              variant={activeTab === 'custom-designs' ? 'default' : 'outline'}
              onClick={() => { setActiveTab('custom-designs'); handleRefreshCustomOrders(); }}
              className={activeTab === 'custom-designs' ? 'bg-violet-600 hover:bg-violet-700' : 'border-violet-200 text-violet-700 hover:bg-violet-50'}
            >
              <ImagePlus className="w-4 h-4 mr-2" />
              Custom Designs ({customOrders.length})
              {customOrders.filter(o => o.status === 'pending_review').length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {customOrders.filter(o => o.status === 'pending_review').length} new
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'coupons' ? 'default' : 'outline'}
              onClick={() => setActiveTab('coupons')}
            >
              <Tag className="w-4 h-4 mr-2" />
              Coupons ({coupons.length})
            </Button>
            <Button
              variant={activeTab === 'bulk-pricing' ? 'default' : 'outline'}
              onClick={() => { setActiveTab('bulk-pricing'); loadBulkPricingTiers(); }}
              className={activeTab === 'bulk-pricing' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}
            >
              <Printer className="w-4 h-4 mr-2" />
              Bulk Pricing
            </Button>
          </div>

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No products yet. Add your first product!</p>
                  </CardContent>
                </Card>
              ) : (
                products.map((product) => (
                  <Card key={product.id} className={!product.is_active ? 'opacity-60' : ''}>
                    <div className="aspect-video bg-gray-100 relative">
                      {product.images?.[0] ? (
                        <img 
                          src={resolveImageUrl(product.images[0])} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CreditCard className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      {!product.is_active && (
                        <Badge className="absolute top-2 left-2 bg-red-500">Inactive</Badge>
                      )}
                      {product.discount_percent > 0 && (
                        <Badge className="absolute top-2 right-2 bg-green-500">
                          {product.discount_percent}% OFF
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                      <p className="text-sm text-gray-500 truncate">{product.category}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-bold text-amber-600">{formatPrice(product.price)}</span>
                        <span className="text-sm text-gray-400">Stock: {product.stock}</span>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => openEditDialog(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => copyProduct(product)}
                          data-testid={`copy-product-${product.id}`}
                          title="Copy Product"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteProduct(product.id)}
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              {orders.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No orders yet</p>
                  </CardContent>
                </Card>
              ) : (
                orders.map((order) => (
                  <Card key={order.id} className="overflow-hidden">
                    {/* Order Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-lg">#{order.order_number}</h3>
                            <Badge className={
                              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'confirmed' ? 'bg-purple-100 text-purple-800' :
                              order.status === 'processing' ? 'bg-indigo-100 text-indigo-800' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {order.status?.toUpperCase()}
                            </Badge>
                            <Badge className={
                              order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }>
                              {order.payment_status === 'paid' ? '💰 PAID' : '⏳ UNPAID'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Placed on {formatDate(order.created_at)}
                            {order.paid_at && ` • Paid on ${formatDate(order.paid_at)}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-600">{formatPrice(order.total)}</p>
                        <p className="text-sm text-gray-500">{order.items?.length || 0} item(s)</p>
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Customer & Address Info */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">📍</span>
                            Shipping Address
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                            <p className="font-medium text-gray-900">{order.address?.full_name}</p>
                            <p className="text-sm text-gray-600">{order.address?.address_line1}</p>
                            {order.address?.address_line2 && (
                              <p className="text-sm text-gray-600">{order.address?.address_line2}</p>
                            )}
                            <p className="text-sm text-gray-600">
                              {order.address?.city}, {order.address?.state} - {order.address?.pincode}
                            </p>
                            <p className="text-sm text-gray-600">{order.address?.country || 'India'}</p>
                            <div className="pt-2 border-t mt-2">
                              <p className="text-sm">
                                <span className="text-gray-500">Phone:</span>{' '}
                                <a href={`tel:${order.address?.phone}`} className="text-blue-600 font-medium">
                                  {order.address?.phone}
                                </a>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-500">Email:</span>{' '}
                                <a href={`mailto:${order.user_email}`} className="text-blue-600">
                                  {order.user_email}
                                </a>
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Order Items */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">📦</span>
                            Order Items
                          </h4>
                          <div className="space-y-3">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                                <div className="w-14 h-14 bg-white rounded-lg overflow-hidden flex-shrink-0 border">
                                  {item.product_image ? (
                                    <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <CreditCard className="w-6 h-6 text-gray-300" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                                  <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                                  <p className="text-sm font-medium text-amber-600">{formatPrice(item.item_total)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Order Summary */}
                          <div className="mt-4 pt-4 border-t space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Subtotal</span>
                              <span>{formatPrice(order.subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Shipping</span>
                              <span className="text-green-600">{order.shipping === 0 ? 'Free' : formatPrice(order.shipping)}</span>
                            </div>
                            <div className="flex justify-between font-semibold pt-2 border-t">
                              <span>Total</span>
                              <span className="text-amber-600">{formatPrice(order.total)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment Info */}
                      {order.razorpay_payment_id && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-xs text-gray-500">
                            <strong>Payment ID:</strong> {order.razorpay_payment_id}
                          </p>
                        </div>
                      )}
                      
                      {/* Coupon Info */}
                      {order.coupon_details && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 text-green-600">
                            <Tag className="w-4 h-4" />
                            <span className="text-sm">
                              Coupon Applied: <strong>{order.coupon_details.code}</strong> 
                              ({order.coupon_details.discount_percent}% off - Saved ₹{order.coupon_details.discount_amount})
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Update Status:</span>
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="processing">Processing</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteOrder(order.id, order.order_number)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Order
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === 'custom-designs' && (
            <div className="space-y-4">
              {/* Header bar */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <ImagePlus className="w-5 h-5 text-violet-500" />
                    Custom Design Orders
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">Users who uploaded their own visiting card designs for printing</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRefreshCustomOrders} disabled={customOrdersLoading}>
                  {customOrdersLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Refresh
                </Button>
              </div>

              {/* Summary badges */}
              {customOrders.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'All', status: null, color: 'bg-gray-100 text-gray-700' },
                    { label: 'Pending Review', status: 'pending_review', color: 'bg-yellow-100 text-yellow-700' },
                    { label: 'Contacted', status: 'contacted', color: 'bg-blue-100 text-blue-700' },
                    { label: 'Payment Done', status: 'payment_received', color: 'bg-emerald-100 text-emerald-700' },
                    { label: 'Printing', status: 'printing', color: 'bg-violet-100 text-violet-700' },
                    { label: 'Shipped', status: 'shipped', color: 'bg-indigo-100 text-indigo-700' },
                    { label: 'Completed', status: 'completed', color: 'bg-green-100 text-green-700' },
                  ].map(({ label, status, color }) => {
                    const cnt = status ? customOrders.filter(o => o.status === status).length : customOrders.length;
                    return cnt > 0 ? (
                      <span key={label} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
                        {label}: {cnt}
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {customOrdersLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                </div>
              ) : customOrders.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <ImagePlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No custom design orders yet</p>
                    <p className="text-sm text-gray-400 mt-1">Orders will appear here when users upload their designs</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {customOrders.map((order) => (
                    <Card key={order.id} className="overflow-hidden border-l-4 border-l-violet-400">
                      {/* Order Header */}
                      <div className="bg-violet-50 px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <ImagePlus className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900 text-sm">
                                {order.user_name || order.user_email?.split('@')[0] || 'Customer'}
                              </span>
                              <span className="text-xs text-gray-400 font-mono">#{order.id?.slice(0, 8).toUpperCase()}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                              <span>{order.user_email}</span>
                              {order.phone && (
                                <a href={`tel:${order.phone}`} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium">
                                  <Phone className="w-3 h-3" />{order.phone}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Status badge */}
                          <StatusBadge status={order.status} />
                          {/* Timestamp */}
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      </div>

                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row gap-4">
                          {/* Left: Files */}
                          <div className="flex-1">
                            <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
                              <FileImage className="w-3.5 h-3.5 text-violet-500" />
                              Uploaded Files ({order.files?.length || 0})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {(order.files || []).map((file, idx) => (
                                <FilePreviewCard
                                  key={file.id || idx}
                                  file={file}
                                  index={idx}
                                  getDownloadUrl={getFileDownloadUrl}
                                  token={null}
                                  user={user}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Right: Details + Actions */}
                          <div className="lg:w-72 space-y-3">
                            {/* Order details */}
                            <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1.5">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Price</span>
                                <span className="font-bold text-gray-900">₹{order.price || 999}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Prints</span>
                                <span className="font-semibold text-emerald-700 flex items-center gap-1">
                                  <Printer className="w-3 h-3" />{order.quantity || 100} cards
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Files</span>
                                <span className="font-medium text-gray-700">{order.files?.length || 0} file(s)</span>
                              </div>
                            </div>

                            {/* Notes */}
                            {order.notes && (
                              <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-xs">
                                <p className="font-semibold text-amber-700 mb-0.5">Customer Notes:</p>
                                <p className="text-amber-600">{order.notes}</p>
                              </div>
                            )}

                            {/* Status update */}
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-1.5">Update Status</p>
                              <div className="flex flex-wrap gap-1.5">
                                {[
                                  { v: 'contacted', label: 'Contacted', color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
                                  { v: 'payment_received', label: 'Payment ✓', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
                                  { v: 'printing', label: 'Printing', color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
                                  { v: 'shipped', label: 'Shipped', color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' },
                                  { v: 'completed', label: 'Completed', color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
                                  { v: 'cancelled', label: 'Cancel', color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
                                ].map(({ v, label, color }) => (
                                  <button
                                    key={v}
                                    onClick={() => handleUpdateCustomOrderStatus(order.id, v)}
                                    disabled={order.status === v || updatingOrderStatus[order.id]}
                                    className={`px-2.5 py-1 rounded-lg border text-[10px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${color} ${order.status === v ? 'ring-1 ring-offset-1 ring-current' : ''}`}
                                  >
                                    {updatingOrderStatus[order.id] && order.status !== v ? <Loader2 className="w-2.5 h-2.5 animate-spin inline" /> : label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* WhatsApp quick contact */}
                            {order.phone && (
                              <a
                                href={`https://wa.me/91${order.phone}?text=${encodeURIComponent(`Hi ${order.user_name || 'there'}, we received your visiting card design order (#${order.id?.slice(0,8).toUpperCase()}). We'll get back to you shortly with payment details. 100 printed cards @ ₹999. - Piper Card Team`)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp Customer
                              </a>
                            )}

                            {/* Delete Order Button */}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteCustomOrder(order.id)}
                              className="w-full bg-red-500 hover:bg-red-600 text-xs"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete Order
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === 'coupons' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => openCouponDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Coupon
                </Button>
              </div>
              
              {coupons.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No coupons yet. Create your first coupon!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {coupons.map((coupon) => (
                    <Card key={coupon.id} className={`${!coupon.is_active ? 'opacity-60' : ''}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg font-mono">{coupon.code}</CardTitle>
                            <p className="text-sm text-gray-500 mt-1">
                              {coupon.product_name || 'All Products'}
                            </p>
                          </div>
                          <Badge className={coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                            {coupon.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-2xl font-bold text-amber-600">
                            <Percent className="w-5 h-5" />
                            {coupon.discount_percent}% OFF
                          </div>
                          
                          <div className="text-sm text-gray-600 space-y-1">
                            {coupon.min_order_value && (
                              <p>Min Order: ₹{coupon.min_order_value}</p>
                            )}
                            {coupon.max_discount && (
                              <p>Max Discount: ₹{coupon.max_discount}</p>
                            )}
                            {coupon.usage_limit && (
                              <p>Usage: {coupon.used_count || 0}/{coupon.usage_limit}</p>
                            )}
                            {coupon.expiry_date && (
                              <p>Expires: {new Date(coupon.expiry_date).toLocaleDateString()}</p>
                            )}
                            <p>{coupon.one_time_per_user ? 'One-time per user' : 'Multiple use allowed'}</p>
                          </div>
                          
                          {coupon.description && (
                            <p className="text-xs text-gray-500 italic">{coupon.description}</p>
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-4 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => openCouponDialog(coupon)}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bulk Pricing Tab */}
          {activeTab === 'bulk-pricing' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Quantity Discount Tiers</h3>
                  <p className="text-sm text-gray-500">Set quantity-based discounts for bulk orders</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadBulkPricingTiers} disabled={loadingBulkPricing}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingBulkPricing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={openNewTierDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tier
                  </Button>
                </div>
              </div>
              
              {loadingBulkPricing ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">Loading discount tiers...</p>
                  </CardContent>
                </Card>
              ) : bulkPricingTiers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Printer className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No discount tiers yet. Create your first tier!</p>
                    <p className="text-xs text-gray-400 mt-2">Default tiers will be created when first order is placed.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {bulkPricingTiers.sort((a, b) => a.min_quantity - b.min_quantity).map((tier) => (
                    <Card key={tier.id} className={!tier.is_active ? 'opacity-50' : ''}>
                      <CardContent className="pt-6">
                        <div className="text-center mb-4">
                          <div className="text-xs text-gray-500 font-medium mb-1">Quantity</div>
                          <div className="text-xl font-bold text-gray-900">
                            {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'}
                          </div>
                        </div>
                        <div className="text-center mb-4">
                          <div className="text-xs text-gray-500 font-medium mb-1">Discount</div>
                          <div className="text-2xl font-bold text-emerald-600">
                            {tier.discount_percent}%
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => openEditTierDialog(tier)}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteTier(tier.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Pricing Preview */}
              <Card className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-sm text-emerald-800">Discount Preview (as shown to customers)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {bulkPricingTiers.sort((a, b) => a.min_quantity - b.min_quantity).map((tier, i) => (
                      <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center min-w-[100px]">
                        <p className="text-xs text-gray-500 font-medium">
                          {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'} cards
                        </p>
                        <p className="text-lg font-bold text-emerald-600">{tier.discount_percent}% off</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Pricing Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingTier ? 'Edit Pricing Tier' : 'Add Pricing Tier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Minimum Quantity *</Label>
              <Input
                type="number"
                min={1}
                value={tierForm.min_quantity}
                onChange={(e) => setTierForm(prev => ({ ...prev, min_quantity: e.target.value }))}
                placeholder="e.g., 2"
              />
            </div>
            <div>
              <Label>Maximum Quantity (leave empty for unlimited)</Label>
              <Input
                type="number"
                min={1}
                value={tierForm.max_quantity}
                onChange={(e) => setTierForm(prev => ({ ...prev, max_quantity: e.target.value }))}
                placeholder="e.g., 10 (or empty for 100+)"
              />
            </div>
            <div>
              <Label>Discount Percent (%) *</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step="1"
                value={tierForm.discount_percent}
                onChange={(e) => setTierForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                placeholder="e.g., 10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
            <Button onClick={saveTier} className="bg-emerald-600 hover:bg-emerald-700">
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product Name *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Premium NFC Card"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Product description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>MRP (₹)</Label>
                <Input
                  type="number"
                  value={productForm.mrp}
                  onChange={(e) => setProductForm(prev => ({ ...prev, mrp: e.target.value }))}
                  placeholder="799"
                />
              </div>
              <div>
                <Label>Selling Price (₹) *</Label>
                <Input
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="499"
                />
              </div>
              <div>
                <Label>Discount %</Label>
                <Input
                  type="number"
                  value={productForm.discount_percent}
                  onChange={(e) => setProductForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input
                  value={productForm.category}
                  onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="NFC Card"
                />
              </div>
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm(prev => ({ ...prev, stock: e.target.value }))}
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <Label>Features (one per line)</Label>
              <Textarea
                value={productForm.features}
                onChange={(e) => setProductForm(prev => ({ ...prev, features: e.target.value }))}
                placeholder="Premium quality&#10;NFC enabled&#10;1 year warranty"
                rows={3}
              />
            </div>
            <div>
              <Label>Product Images</Label>
              
              {/* File Upload Button */}
              <div className="mb-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Images
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-1">Supports JPEG, PNG, WebP, GIF (max 5MB each)</p>
              </div>

              {/* OR add URL */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400">OR add URL</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              
              <div className="flex gap-2 mb-3">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Enter image URL"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addImageUrl}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Image Preview */}
              {productForm.images.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                  {productForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img} 
                        alt="" 
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200" 
                        onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>'; }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {idx === 0 && (
                        <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">
                          Main
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={productForm.is_active}
                onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProduct}>
              {editingProduct ? 'Update' : 'Create'} Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Coupon Code *</Label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="e.g., SAVE20"
                disabled={!!editingCoupon}
              />
              {editingCoupon && <p className="text-xs text-gray-500 mt-1">Code cannot be changed after creation</p>}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Percent *</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={couponForm.discount_percent}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                  placeholder="20"
                />
              </div>
              <div>
                <Label>For Product</Label>
                <Select
                  value={couponForm.product_id || 'all'}
                  onValueChange={(value) => setCouponForm(prev => ({ ...prev, product_id: value === 'all' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products.filter(p => p.is_active !== false).map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Order Value (₹)</Label>
                <Input
                  type="number"
                  value={couponForm.min_order_value}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, min_order_value: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Max Discount (₹)</Label>
                <Input
                  type="number"
                  value={couponForm.max_discount}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, max_discount: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usage Limit</Label>
                <Input
                  type="number"
                  value={couponForm.usage_limit}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, usage_limit: e.target.value }))}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={couponForm.expiry_date}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Description (Internal Note)</Label>
              <Input
                value={couponForm.description}
                onChange={(e) => setCouponForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Summer sale promotion"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={couponForm.one_time_per_user}
                  onCheckedChange={(checked) => setCouponForm(prev => ({ ...prev, one_time_per_user: checked }))}
                />
                <Label className="cursor-pointer">One-time per user</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={couponForm.is_active}
                  onCheckedChange={(checked) => setCouponForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label className="cursor-pointer">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCoupon}>
              {editingCoupon ? 'Update' : 'Create'} Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminProducts;

/* ─── StatusBadge ─────────────────────────────────────────── */
const STATUS_STYLES = {
  pending_review:  { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pending Review', dot: 'bg-yellow-400' },
  contacted:       { bg: 'bg-blue-100 text-blue-800 border-blue-200',   label: 'Contacted',      dot: 'bg-blue-400'   },
  payment_received:{ bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Payment Done', dot: 'bg-emerald-400' },
  printing:        { bg: 'bg-violet-100 text-violet-800 border-violet-200', label: 'Printing',     dot: 'bg-violet-400' },
  shipped:         { bg: 'bg-indigo-100 text-indigo-800 border-indigo-200', label: 'Shipped',      dot: 'bg-indigo-400' },
  completed:       { bg: 'bg-green-100 text-green-800 border-green-200',  label: 'Completed',     dot: 'bg-green-500'  },
  cancelled:       { bg: 'bg-red-100 text-red-800 border-red-200',       label: 'Cancelled',     dot: 'bg-red-400'    },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { bg: 'bg-gray-100 text-gray-700 border-gray-200', label: status || 'Unknown', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${s.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

/* ─── FilePreviewCard ─────────────────────────────────────── */
const API_URL_PREVIEW = process.env.REACT_APP_BACKEND_URL;

const FilePreviewCard = ({ file, index, user }) => {
  const [downloading, setDownloading] = React.useState(false);
  const isPDF = file.content_type === 'application/pdf';
  const isImage = file.content_type?.startsWith('image/');
  const label = index === 0 ? (file.filename?.toLowerCase().includes('back') ? 'Back' : 'Front / Combined') : 'Back';

  const handleDownload = async () => {
    if (!user) return;
    setDownloading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL_PREVIEW}/api/shop/custom-design/files/${file.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || `design-${index + 1}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error:', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm min-w-0">
      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPDF ? 'bg-red-50' : 'bg-blue-50'}`}>
        {isPDF
          ? <span className="text-red-500 text-[10px] font-black">PDF</span>
          : <FileImage className="w-4 h-4 text-blue-500" />
        }
      </div>
      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-800 truncate max-w-[120px]">{file.filename || `file-${index+1}`}</p>
        <p className="text-[10px] text-gray-400">{label}</p>
      </div>
      {/* Download */}
      <button
        onClick={handleDownload}
        disabled={downloading}
        title="Download file"
        className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-50 hover:bg-violet-100 flex items-center justify-center transition-colors"
      >
        {downloading
          ? <Loader2 className="w-3.5 h-3.5 text-violet-500 animate-spin" />
          : <Download className="w-3.5 h-3.5 text-gray-500 hover:text-violet-600" />
        }
      </button>
    </div>
  );
};
